/**
 * WristbandSyncDialog
 *
 * 4-step wristband assignment / re-sync dialog.
 * Can be embedded at check-in (skippable) or from a person/guest profile.
 *
 * Flow:
 *   1. Lectura  — wait for NFC card read event from connector
 *   2. Checkeo  — verify the chip is not already assigned to another person
 *   3. Escritura — write a logical wristband credential to chip via NFC connector
 *   4. Guardado  — save wristband record in backend; deactivate old if re-sync
 *
 * If `existingWristband` is supplied the user must provide a re-sync reason
 * before the process starts, and the old wristband is deactivated after save.
 */

import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { useTranslation } from '@/core';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/theme/components/ui/dialog';
import { Button } from '@/theme/components/ui/button';
import { Label } from '@/theme/components/ui/label';
import { Textarea } from '@/theme/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/theme/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/theme/components/ui/collapsible';
import { cn } from '@/lib/utils';
import {
  AlertTriangle,
  ArrowUpRight,
  BatteryMedium,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Circle,
  Download,
  HelpCircle,
  Loader2,
  LogIn,
  MonitorX,
  Nfc,
  RefreshCw,
  SkipForward,
  Usb,
  Volume2,
  Wifi,
  WifiOff,
  XCircle,
} from 'lucide-react';
import trayIconMacUrl from '@/assets/shaka-bridge-tray-mac.png';
import trayIconWinUrl from '@/assets/shaka-bridge-tray-win.png';
import { nfcConnector } from '@/shared/services/nfcConnectorService';
import {
  lookupWristband,
  assignWristband,
  deactivateWristband,
  type Wristband,
} from '@/shared/services/wristbandService';
import { isFutureDay } from '@/shared/utils';
import { mergePersons, type Person } from '@/entities/person';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type StepState = 'idle' | 'running' | 'done' | 'error';

interface Step {
  id: 'read' | 'check' | 'write' | 'save';
  label: string;
  detail: string;
  state: StepState;
  errorMsg?: string;
}

type DialogPhase = 'reason' | 'connector' | 'steps' | 'merge_confirm' | 'success' | 'error' | 'unavailable';

/** Why the `unavailable` phase was entered — drives which help is shown. */
type UnavailableKind = 'bridge' | 'reader';

// Resync reasons — labels are i18n keys resolved at render time
const RESYNC_REASON_KEYS = [
  { value: 'lost',    labelKey: 'wristband.reason.lost' },
  { value: 'damaged', labelKey: 'wristband.reason.damaged' },
  { value: 'routine', labelKey: 'wristband.reason.routine' },
  { value: 'other',   labelKey: 'wristband.reason.other' },
];

// Scan/write timeouts (ms) — timeout messages are i18n keys
const NFC_SCAN_TIMEOUT_MS = 60_000;  // 60s
const NFC_WRITE_TIMEOUT_MS = 60_000; // 60s
const NFC_SCAN_TIMEOUT_MESSAGE_KEY = 'nfc.sync.scanTimeout';
const NFC_WRITE_TIMEOUT_MESSAGE_KEY = 'nfc.sync.writeTimeout';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** Status of the stay the band is being paired for, when there is one.
 *  Mirrors BookingGuestStay.status without importing from a module — this
 *  dialog is shared and must not depend on `modules/booking`. */
export type WristbandStayStatus =
  | 'expected'
  | 'checked_in'
  | 'checked_out'
  | 'cancelled'
  | 'no_show';

export interface WristbandSyncDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  personId: string;
  personName: string;
  /** Location to anchor the band to. `null` for a company-scope person —
   *  the band is a person identifier that resolves tenant-wide. */
  locationId: string | null;
  /** If the person already has an active wristband, show re-sync reason step. */
  existingWristband?: Wristband | null;
  /** If true (check-in flow), show a "Skip" button. */
  skippable?: boolean;
  onSkip?: () => void;
  onSuccess: (wristband: Wristband) => void;
  /** The guest's stay, when pairing from a booking surface. A band paired on
   *  a stay that is not `checked_in` looks fine at the reader but the POS
   *  refuses room charge — only a live check-in enables it. Pass this and the
   *  dialog says so instead of letting the gap ship silently. Omit for staff
   *  or company-scope people, where no stay is involved. */
  stayStatus?: WristbandStayStatus | null;
  /** Arrival date of that stay, used to tell "arrives later" from "should
   *  already be checked in". */
  stayArrivalDate?: Date | string | null;
  /** Invoked from the warning's CTA — the caller opens its own check-in
   *  surface. Without it the warning is informational only. */
  onCheckIn?: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function initSteps(t: (k: string) => string): Step[] {
  return [
    { id: 'read',  label: t('wristband.step.read'),  detail: t('wristband.step.readDetail'),  state: 'idle' },
    { id: 'check', label: t('wristband.step.check'), detail: t('wristband.step.checkDetail'), state: 'idle' },
    { id: 'write', label: t('wristband.step.write'), detail: t('wristband.step.writeDetail'), state: 'idle' },
    { id: 'save',  label: t('wristband.step.save'),  detail: t('wristband.step.saveDetail'),  state: 'idle' },
  ];
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StepRow({ step, index }: { step: Step; index: number }) {
  const icon = (() => {
    switch (step.state) {
      case 'running': return <Loader2 className="h-5 w-5 animate-spin text-primary" />;
      case 'done':    return <CheckCircle2 className="h-5 w-5 text-success" />;
      case 'error':   return <XCircle className="h-5 w-5 text-destructive" />;
      default:        return <Circle className="h-5 w-5 text-muted-foreground/40" />;
    }
  })();

  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={cn(
            'text-sm font-medium',
            step.state === 'idle'    && 'text-muted-foreground',
            step.state === 'running' && 'text-foreground',
            step.state === 'done'    && 'text-success',
            step.state === 'error'   && 'text-destructive',
          )}>
            {index + 1}. {step.label}
          </span>
          {step.state === 'running' && (
            <span className="text-xs text-muted-foreground">{step.detail}</span>
          )}
        </div>
        {step.state === 'error' && step.errorMsg && (
          <p className="mt-0.5 text-xs text-destructive">{step.errorMsg}</p>
        )}
      </div>
    </div>
  );
}

function ConnectorStatus({ status }: { status: string }) {
  const { t } = useTranslation();
  const connected = status === 'connected';
  return (
    <div className={cn(
      'flex items-center gap-2 rounded-md px-3 py-2 text-xs',
      connected ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground',
    )}>
      {connected
        ? <Wifi className="h-3.5 w-3.5" />
        : <WifiOff className="h-3.5 w-3.5" />}
      {connected ? t('wristband.connector.connected') : t('wristband.connector.disconnected')}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bridge-offline help — shown in the `unavailable` phase
// ---------------------------------------------------------------------------

/** One numbered row inside the troubleshooting checklist. */
function TroubleStep({ n, children }: { n: number; children: ReactNode }) {
  return (
    <div className="flex gap-2.5">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-muted-foreground">
        {n}
      </span>
      <div className="min-w-0 flex-1 space-y-2 text-[12.5px] leading-relaxed">{children}</div>
    </div>
  );
}

/**
 * Mini menu-bar / system-tray render with the real Shaka Bridge tray icons
 * (copied from shaka-bridge `systray/`), so staff knows exactly what to look
 * for. Clock/wifi/battery glyphs are decorative context, not live data.
 */
function TrayLocationPreview() {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5">
      {/* macOS menu bar */}
      <div className="flex items-center gap-2.5">
        <span className="w-14 shrink-0 text-right text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          macOS
        </span>
        <div
          aria-hidden="true"
          className="flex h-8 min-w-0 flex-1 items-center justify-end gap-3 overflow-hidden rounded-md border border-border bg-muted/60 px-3"
        >
          <Wifi className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
          <span className="shrink-0 rounded-md px-1.5 py-1 outline outline-2 outline-offset-1 outline-coral-500">
            {/* Template icon is black — invert to white in dark mode like macOS does */}
            <img src={trayIconMacUrl} alt="" className="h-3.5 w-auto dark:invert" />
          </span>
          <BatteryMedium className="h-4 w-4 shrink-0 text-muted-foreground/70" />
          <span className="whitespace-nowrap text-[11px] text-muted-foreground">14:32</span>
        </div>
      </div>

      {/* Windows system tray — depicted dark like the real taskbar */}
      <div className="flex items-center gap-2.5">
        <span className="w-14 shrink-0 text-right text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Windows
        </span>
        <div
          aria-hidden="true"
          className="flex h-8 min-w-0 flex-1 items-center justify-end gap-3 overflow-hidden rounded-md border border-zinc-700 bg-zinc-800 px-3"
        >
          <ChevronUp className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
          <span className="shrink-0 rounded-md px-1.5 py-1 outline outline-2 outline-offset-1 outline-coral-500">
            <img src={trayIconWinUrl} alt="" className="h-3.5 w-auto" />
          </span>
          <Wifi className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
          <Volume2 className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
          <span className="whitespace-nowrap text-[11px] text-zinc-400">14:32</span>
        </div>
      </div>

      <p className="flex items-center justify-end gap-1 text-[11.5px] font-medium text-coral-700 dark:text-coral-400">
        <ArrowUpRight className="h-3.5 w-3.5" />
        {t('nfc.sync.bridge.trayCaption')}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Horizontal stepper — mirrors V3 of shaka-deck-mockup-checkin.html
// ---------------------------------------------------------------------------

interface HorizontalStepDef {
  num: string;
  label: string;
  state: StepState;
}

function HorizontalStepper({ steps }: { steps: HorizontalStepDef[] }) {
  return (
    <div className="flex items-center gap-2 overflow-x-auto rounded-md border border-border bg-card px-3 py-2.5">
      {steps.map((s, i) => {
        const done = s.state === 'done';
        const active = s.state === 'running';
        return (
          <div key={s.num} className="flex flex-1 items-center gap-2">
            <div
              className={cn(
                'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full font-mono text-[10px] font-semibold',
                done
                  ? 'bg-sage-500 text-white'
                  : active
                  ? 'bg-ocean-700 text-white'
                  : 'bg-sand-200 text-sand-700',
              )}
            >
              {done ? '✓' : s.num}
            </div>
            <div
              className={cn(
                'min-w-0 flex-1 text-[11.5px] font-medium',
                done
                  ? 'text-sage-700'
                  : active
                  ? 'text-ocean-700'
                  : 'text-muted-foreground',
              )}
            >
              {s.label}
            </div>
            {i < steps.length - 1 ? (
              <span aria-hidden="true" className="hidden h-px w-4 flex-shrink-0 bg-sand-300 sm:block" />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function WristbandSyncDialog({
  open,
  onOpenChange,
  personId,
  personName,
  locationId,
  existingWristband,
  skippable,
  onSkip,
  onSuccess,
  stayStatus,
  stayArrivalDate,
  onCheckIn,
}: WristbandSyncDialogProps) {
  const { t } = useTranslation();

  // Phase state
  const [phase, setPhase] = useState<DialogPhase>(existingWristband ? 'reason' : 'connector');
  const [connectorStatus, setConnectorStatus] = useState(nfcConnector.status);
  const [unavailableKind, setUnavailableKind] = useState<UnavailableKind>('bridge');

  // Re-sync reason
  const [reason, setReason] = useState('');
  const [reasonNotes, setReasonNotes] = useState('');

  // Steps
  const [steps, setSteps] = useState<Step[]>(() => initSteps(t));
  const [globalError, setGlobalError] = useState('');
  const [successWristband, setSuccessWristband] = useState<Wristband | null>(null);

  // Merge flow
  const [conflictPerson, setConflictPerson] = useState<Person | null>(null);
  const [pendingIdentifier, setPendingIdentifier] = useState('');

  // Abort controller for ongoing process
  const abortRef = useRef<{ aborted: boolean }>({ aborted: false });

  // ---------------------------------------------------------------------------
  // Reset when dialog opens / signal busy state to global NFC indicator
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!open) return;
    abortRef.current = { aborted: false };
    setPhase(existingWristband ? 'reason' : 'connector');
    setSteps(initSteps(t));
    setGlobalError('');
    setSuccessWristband(null);
    setReason('');
    setReasonNotes('');
    return () => { nfcConnector.setBusy(false); };
  }, [open]);

  // Mark connector as busy while sync steps are running
  useEffect(() => {
    nfcConnector.setBusy(phase === 'steps');
  }, [phase]);

  // ---------------------------------------------------------------------------
  // Connector status tracking
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!open) return;
    const onConn = () => setConnectorStatus('connected');
    const onDisc = () => setConnectorStatus('disconnected');
    nfcConnector.on('connected', onConn);
    nfcConnector.on('disconnected', onDisc);
    setConnectorStatus(nfcConnector.status);
    return () => {
      nfcConnector.off('connected', onConn);
      nfcConnector.off('disconnected', onDisc);
    };
  }, [open]);

  // ---------------------------------------------------------------------------
  // Step helpers
  // ---------------------------------------------------------------------------

  const setStepState = (id: Step['id'], state: StepState, errorMsg?: string) => {
    setSteps(prev => prev.map(s => s.id === id ? { ...s, state, errorMsg } : s));
  };

  const getErrorStatus = (error: unknown): number | undefined => (
    typeof error === 'object' && error !== null
      ? (
          (error as import('@/types/global').ApiError).status
          ?? ((error as { code?: string }).code === 'NOT_FOUND' ? 404 : undefined)
        )
      : undefined
  );

  const getErrorMessage = (error: unknown, fallback: string): string => (
    typeof error === 'object' && error !== null && 'message' in error && typeof (error as { message?: unknown }).message === 'string'
      ? (error as { message: string }).message
      : fallback
  );

  // ---------------------------------------------------------------------------
  // Connect to NFC connector
  // ---------------------------------------------------------------------------

  const connectToNfc = useCallback(async () => {
    setPhase('connector');
    setGlobalError('');
    try {
      await nfcConnector.connect();
      setConnectorStatus('connected');
      setPhase('steps');
      startProcess();
    } catch {
      setConnectorStatus('disconnected');
      setUnavailableKind('bridge');
      setPhase('unavailable');
    }
  }, []);

  // If no existing wristband, auto-connect on mount
  useEffect(() => {
    if (!open || existingWristband) return;
    connectToNfc();
  }, [open]);

  // ---------------------------------------------------------------------------
  // Main 4-step process
  // ---------------------------------------------------------------------------

  /** Continue from step 3 onward by writing a fresh logical credential to the card. */
  const continueFromWrite = useCallback(async () => {
    const abort = abortRef.current;
    const device = nfcConnector.firstNfcDevice;
    const credentialId = crypto.randomUUID();

    // ── Step 3: Escritura ────────────────────────────────────────────────────
    setStepState('write', 'running');

    let writeSucceeded = false;
    try {
      if (!device) throw new Error(t('nfc.sync.noReader'));
      await nfcConnector.writeCard(device.id, credentialId);
      if (abort.aborted) return;
      writeSucceeded = true;
      setStepState('write', 'done');
    } catch (e) {
      if (abort.aborted) return;
      const errorMsg = (e as Error).message || t('nfc.sync.writeError');
      // Detect connector disconnect mid-write
      if (nfcConnector.status === 'disconnected' || errorMsg.toLowerCase().includes('disconnect')) {
        setStepState('write', 'error', t('nfc.sync.connectorDisconnected'));
        setGlobalError(t('nfc.sync.writeDisconnectRetry'));
      } else {
        setStepState('write', 'error', errorMsg);
        setGlobalError(t('nfc.sync.writeError'));
      }
      setPhase('error');
      return;
    }

    // Only proceed to save if write succeeded
    if (!writeSucceeded) {
      setGlobalError(t('nfc.sync.writeNotConfirmed'));
      setPhase('error');
      return;
    }

    // ── Step 4: Guardado ─────────────────────────────────────────────────────
    setStepState('save', 'running');

    try {
      if (existingWristband) {
        // Pass the operator-selected re-sync reason so the audit trail records why.
        await deactivateWristband(existingWristband.id, reason || undefined);
      }
      const newWristband = await assignWristband(personId, credentialId, locationId);
      if (abort.aborted) return;
      setStepState('save', 'done');
      setSuccessWristband(newWristband);
      setPhase('success');
      onSuccess(newWristband);
    } catch (e) {
      if (abort.aborted) return;
      setStepState('save', 'error', (e as Error).message);
      setGlobalError(t('nfc.sync.saveError'));
      setPhase('error');
    }
  }, [personId, locationId, existingWristband, t, onSuccess]);

  /** Steps 1 + 2: read chip, check ownership. Pauses at merge_confirm if conflict found. */
  const startProcess = useCallback(async () => {
    const abort = abortRef.current;

    // Pre-flight: NFC reader must be available
    if (!nfcConnector.firstNfcDevice) {
      setUnavailableKind('reader');
      setPhase('unavailable');
      return;
    }

    setPhase('steps');
    setSteps(initSteps(t));
    setGlobalError('');
    setConflictPerson(null);
    setPendingIdentifier('');

    // ── Step 1: Lectura ──────────────────────────────────────────────────────
    setStepState('read', 'idle');

    let lookupIdentifier = '';
    const stopDetectedListener = nfcConnector.once('nfc-card-detected', () => {
      setStepState('read', 'running');
    });
    try {
      const card = await nfcConnector.waitFor<{ uid: string; credential?: string }>('nfc-card-read', NFC_SCAN_TIMEOUT_MS);
      stopDetectedListener();
      if (abort.aborted) return;
      setStepState('read', 'running');
      lookupIdentifier = card.credential || card.uid;
      setStepState('read', 'done');
    } catch (e) {
      stopDetectedListener();
      if (abort.aborted) return;
      const errorMsg = (e as Error).message;
      // Check if timeout
      const isTimeout = errorMsg?.toLowerCase().includes('timeout');
      setStepState('read', 'error', isTimeout ? t(NFC_SCAN_TIMEOUT_MESSAGE_KEY) : errorMsg);
      setGlobalError(isTimeout ? t(NFC_SCAN_TIMEOUT_MESSAGE_KEY) : t('nfc.sync.noCard'));
      setPhase('error');
      return;
    }

    // ── Step 2: Checkeo ──────────────────────────────────────────────────────
    setStepState('check', 'running');

    try {
      const lookup = await lookupWristband(lookupIdentifier);
      if (abort.aborted) return;

      if (lookup.wristband.status === 'active' && lookup.wristband.personId !== personId) {
        setStepState('check', 'done');
        setConflictPerson(lookup.person);
        setPendingIdentifier(lookupIdentifier);
        setPhase('merge_confirm');
        return;
      }
      setStepState('check', 'done');
    } catch (error) {
      if (abort.aborted) return;
      if (getErrorStatus(error) === 404) {
        // 404 = chip not yet in system → new chip, proceed
        setStepState('check', 'done');
      } else {
        setStepState('check', 'error', getErrorMessage(error, t('nfc.sync.checkError')));
        setGlobalError(t('nfc.sync.checkError'));
        setPhase('error');
        return;
      }
    }

    continueFromWrite();
  }, [personId, t, continueFromWrite]);

  // ---------------------------------------------------------------------------
  // Auto-reconnect polling when unavailable
  // NOTE: must be declared AFTER startProcess to avoid Temporal Dead Zone.
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!open || phase !== 'unavailable') return;

    let cancelled = false;

    const tryReconnect = async () => {
      if (cancelled) return;
      try {
        if (nfcConnector.status !== 'connected') {
          await nfcConnector.connect();
        }
        if (cancelled) return;
        setConnectorStatus('connected');
        await new Promise(r => setTimeout(r, 400));
        if (cancelled) return;
        if (nfcConnector.firstNfcDevice) {
          setGlobalError('');
          setPhase('steps');
          setSteps(initSteps(t));
          startProcess();
        } else {
          // Bridge answers but sees no reader — switch the help accordingly
          setUnavailableKind('reader');
        }
      } catch {
        // Still not reachable — keep polling
        setUnavailableKind('bridge');
      }
    };

    const interval = setInterval(tryReconnect, 2000);
    tryReconnect();

    const onConnected = () => { if (!cancelled) tryReconnect(); };
    const onTerminalReady = () => { if (!cancelled) tryReconnect(); };
    nfcConnector.on('connected', onConnected);
    nfcConnector.on('terminal-ready', onTerminalReady);

    return () => {
      cancelled = true;
      clearInterval(interval);
      nfcConnector.off('connected', onConnected);
      nfcConnector.off('terminal-ready', onTerminalReady);
    };
  }, [open, phase, t, startProcess]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleReasonContinue = () => {
    if (!reason) return;
    if (connectorStatus !== 'connected') {
      connectToNfc();
    } else {
      setPhase('steps');
      startProcess();
    }
  };

  const handleMergeConfirm = async () => {
    if (!conflictPerson || !pendingIdentifier) return;
    try {
      setPhase('steps');
      setStepState('save', 'idle');
      setGlobalError('');
      await mergePersons(personId, conflictPerson.id);
      // If merge succeeds, continue with wristband write
      continueFromWrite();
    } catch (error) {
      // Merge failed — rollback UI and show error
      const errorMsg = (error as Error).message || t('nfc.sync.mergeError');
      setStepState('save', 'error', errorMsg);
      setGlobalError(t('nfc.sync.mergeFailed'));
      setPhase('error');
    }
  };

  const handleRetry = () => {
    abortRef.current = { aborted: false };
    if (connectorStatus !== 'connected') {
      connectToNfc();
    } else {
      startProcess();
    }
  };

  const handleClose = () => {
    abortRef.current.aborted = true;
    onOpenChange(false);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const isRunning = phase === 'steps';

  // A band paired on a stay that never gets checked in is the single most
  // common way guests end up unable to charge to their folio (measured in
  // prod 2026-08-19: 3 live stays across Soulac). Pairing BEFORE the arrival
  // date is legitimate (batch pairing exists for it) — the warning separates
  // "too early, fine" from "should be checked in by now, act".
  const stayNotCheckedIn = Boolean(
    stayStatus && stayStatus !== 'checked_in' && stayStatus !== 'checked_out',
  );
  const stayArrivesLater = Boolean(stayNotCheckedIn && isFutureDay(stayArrivalDate));

  const resyncReasons = RESYNC_REASON_KEYS.map(r => ({
    value: r.value,
    label: t(r.labelKey),
  }));

  return (
    // Closing is ALWAYS possible (Esc / X / Cancel) — handleClose aborts the
    // in-flight process. The old `if (!isRunning)` guard made the dialog
    // unclosable whenever the steps phase hung (e.g. waiting for a card that
    // never taps), with no Cancel button in that phase either. Accidental
    // outside-clicks are still ignored while running.
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="sm:max-w-xl" onInteractOutside={(e) => { if (isRunning) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Nfc className="h-5 w-5 text-primary" />
            {existingWristband
              ? t('nfc.sync.resyncTitle')
              : t('nfc.sync.title')
            }
          </DialogTitle>
          <DialogDescription>
            {personName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-1">

          {/* Connector status pill */}
          <ConnectorStatus status={connectorStatus} />

          {/* Stay not checked in — the band will resolve but room charge won't */}
          {stayNotCheckedIn && (
            <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2.5 text-[12px] leading-[1.55]">
              <AlertTriangle className="mt-[1px] h-4 w-4 shrink-0 text-warning" />
              <div className="space-y-2">
                <div>
                  <b>
                    {stayArrivesLater
                      ? t('nfc.sync.notCheckedIn.earlyTitle', 'Pairing ahead of arrival.')
                      : t('nfc.sync.notCheckedIn.title', 'This guest is not checked in.')}
                  </b>{' '}
                  {stayArrivesLater
                    ? t(
                        'nfc.sync.notCheckedIn.earlyBody',
                        'The band is stored now and starts working when the guest is checked in on arrival. Until then the POS will not offer room charge.',
                      )
                    : t(
                        'nfc.sync.notCheckedIn.body',
                        'The band will identify them at the reader, but the POS only offers room charge on a live check-in — charges to their folio will be refused until you check them in.',
                      )}
                </div>
                {onCheckIn && !stayArrivesLater && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7"
                    onClick={() => { abortRef.current.aborted = true; onCheckIn(); }}
                  >
                    <LogIn className="mr-1.5 h-3.5 w-3.5" />
                    {t('nfc.sync.notCheckedIn.action', 'Check in now')}
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* ── Phase: Reason (re-sync only) ─────────────────────────────── */}
          {phase === 'reason' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t('nfc.sync.existingWristbandNote')}
              </p>

              <div className="space-y-2">
                <Label>{t('nfc.sync.reason')}</Label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('nfc.sync.reasonPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {resyncReasons.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground text-xs">
                  {t('common.notes')} ({t('common.optional')})
                </Label>
                <Textarea
                  value={reasonNotes}
                  onChange={e => setReasonNotes(e.target.value)}
                  placeholder={t('nfc.sync.reasonNotes')}
                  rows={2}
                  className="resize-none"
                />
              </div>

              <div className="flex gap-2 justify-end">
                {skippable && (
                  <Button variant="ghost" onClick={onSkip}>
                    <SkipForward className="mr-1.5 h-4 w-4" />
                    {t('common.skip')}
                  </Button>
                )}
                <Button variant="outline" onClick={handleClose}>{t('common.cancel')}</Button>
                <Button onClick={handleReasonContinue} disabled={!reason}>
                  {t('common.next')}
                </Button>
              </div>
            </div>
          )}

          {/* ── Phase: Connecting to connector ───────────────────────────── */}
          {phase === 'connector' && (
            <div className="flex flex-col items-center gap-3 py-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-center text-muted-foreground">
                {t('nfc.sync.connectingBridge')}
              </p>
            </div>
          )}

          {/* ── Phase: Unavailable (bridge not running / no reader) ───────── */}
          {phase === 'unavailable' && (
            <div className="space-y-4">
              <div className="flex flex-col items-center gap-2.5 pt-1 text-center">
                {unavailableKind === 'bridge'
                  ? <MonitorX className="h-9 w-9 text-muted-foreground" />
                  : <Usb className="h-9 w-9 text-muted-foreground" />}
                <p className="text-sm font-semibold">
                  {unavailableKind === 'bridge'
                    ? t('nfc.sync.bridge.notConnectedTitle')
                    : t('nfc.sync.reader.noneTitle')}
                </p>
                <p className="max-w-sm text-[13px] text-muted-foreground">
                  {unavailableKind === 'bridge'
                    ? t('nfc.sync.bridge.notConnectedBody')
                    : t('nfc.sync.reader.noneBody')}
                </p>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {unavailableKind === 'bridge'
                    ? t('nfc.sync.bridge.searching')
                    : t('nfc.sync.reader.searching')}
                </div>
              </div>

              {unavailableKind === 'bridge' && (
                <div className="flex items-center gap-3.5 rounded-lg border border-ocean-200 bg-ocean-50 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-ocean-900">
                      {t('nfc.sync.bridge.downloadTitle')}
                    </p>
                    <p className="text-xs text-ocean-900/70">
                      {t('nfc.sync.bridge.downloadBody')}
                    </p>
                  </div>
                  <Button asChild size="sm" className="shrink-0">
                    <a href="/downloads" target="_blank" rel="noopener noreferrer">
                      <Download className="mr-1.5 h-4 w-4" />
                      {t('nfc.sync.bridge.downloadCta')}
                    </a>
                  </Button>
                </div>
              )}

              <Collapsible defaultOpen className="rounded-lg border border-border">
                <CollapsibleTrigger className="group flex w-full items-center gap-2 px-3.5 py-2.5 text-[13px] font-semibold">
                  <HelpCircle className="h-4 w-4 shrink-0 text-muted-foreground" />
                  {unavailableKind === 'bridge'
                    ? t('nfc.sync.bridge.troubleTitle')
                    : t('nfc.sync.reader.troubleTitle')}
                  <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-3 px-3.5 pb-3.5">
                  {unavailableKind === 'bridge' ? (
                    <>
                      <TroubleStep n={1}>
                        <p>{t('nfc.sync.bridge.step1')}</p>
                        <TrayLocationPreview />
                        <p className="text-xs text-muted-foreground">{t('nfc.sync.bridge.step1Hint')}</p>
                      </TroubleStep>
                      <TroubleStep n={2}>
                        <p>
                          {t('nfc.sync.bridge.step2')}{' '}
                          <span className="text-xs text-muted-foreground">{t('nfc.sync.bridge.step2Hint')}</span>
                        </p>
                      </TroubleStep>
                      <TroubleStep n={3}>
                        <p>{t('nfc.sync.bridge.step3')}</p>
                      </TroubleStep>
                    </>
                  ) : (
                    <>
                      <TroubleStep n={1}>
                        <p>{t('nfc.sync.reader.step1')}</p>
                      </TroubleStep>
                      <TroubleStep n={2}>
                        <p>
                          {t('nfc.sync.reader.step2')}{' '}
                          <span className="text-xs text-muted-foreground">{t('nfc.sync.reader.step2Hint')}</span>
                        </p>
                      </TroubleStep>
                    </>
                  )}
                </CollapsibleContent>
              </Collapsible>

              <div className="flex gap-2 justify-end">
                {skippable && (
                  <Button variant="ghost" onClick={() => { abortRef.current.aborted = true; onSkip?.(); }}>
                    <SkipForward className="mr-1.5 h-4 w-4" />
                    {t('common.skip')}
                  </Button>
                )}
                <Button variant="outline" onClick={handleClose}>{t('common.cancel')}</Button>
                <Button variant="outline" onClick={() => { abortRef.current = { aborted: false }; connectToNfc(); }}>
                  <RefreshCw className="mr-1.5 h-4 w-4" />
                  {t('common.retry')}
                </Button>
              </div>
            </div>
          )}

          {/* ── Phase: Merge confirmation ─────────────────────────────────── */}
          {phase === 'merge_confirm' && conflictPerson && (
            <div className="space-y-4">
              <div className="space-y-4">
                {steps.map((step, i) => (
                  <StepRow key={step.id} step={step} index={i} />
                ))}
              </div>

              <div className="rounded-lg border border-warning/30 bg-warning/5 px-4 py-3 space-y-1">
                <p className="text-sm font-medium">
                  {t('nfc.sync.mergeTitle')}
                </p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{conflictPerson.name}</span>
                  {conflictPerson.email ? ` · ${conflictPerson.email}` : ''}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('nfc.sync.mergeDesc')}
                </p>
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setPendingIdentifier(''); setConflictPerson(null); startProcess(); }}>
                  {t('nfc.sync.useOther')}
                </Button>
                <Button onClick={handleMergeConfirm}>
                  {t('nfc.sync.mergeConfirm')}
                </Button>
              </div>
            </div>
          )}

          {/* ── Phase: Steps ─────────────────────────────────────────────── */}
          {(phase === 'steps' || phase === 'success') && (
            <>
              {/* Horizontal 4-step indicator (V3 mockup) */}
              <HorizontalStepper
                steps={[
                  {
                    num: '01',
                    label: t('nfc.sync.stepper.checkReader', 'Check reader'),
                    state: connectorStatus === 'connected' ? 'done' : 'idle',
                  },
                  {
                    num: '02',
                    label: t('nfc.sync.stepper.tapBand', 'Tap the band'),
                    state: steps[0]?.state ?? 'idle',
                  },
                  {
                    num: '03',
                    label: t('nfc.sync.stepper.confirmUid', 'Confirm UID'),
                    state: steps[1]?.state ?? 'idle',
                  },
                  {
                    num: '04',
                    label: t('nfc.sync.stepper.bindStay', 'Bind to stay'),
                    state: steps[3]?.state ?? 'idle',
                  },
                ]}
              />

              {phase === 'steps' && (
                <div className="flex flex-col items-center gap-3 rounded-lg border border-coral-200 bg-coral-50 px-4 py-5 text-center">
                  <Nfc className="h-10 w-10 text-coral-700" />
                  <p className="text-sm font-medium text-coral-700">
                    {t('nfc.sync.instruction', 'Hold the wristband within 2 cm of the reader.')}
                  </p>
                </div>
              )}

              <div className="space-y-4">
                {steps.map((step, i) => (
                  <StepRow key={step.id} step={step} index={i} />
                ))}
              </div>

              {phase === 'steps' ? (
                <div className="flex items-start gap-2 rounded-md border border-ocean-200 bg-ocean-50 px-3 py-2.5 text-[12px] leading-[1.55] text-ocean-900">
                  <span aria-hidden="true" className="text-[14px] leading-none">ℹ</span>
                  <div>
                    <b>{t('nfc.sync.singleBand.title', 'Single-band rule.')}</b>{' '}
                    {t(
                      'nfc.sync.singleBand.body',
                      'Each stay has one active band. Re-pairing replaces the previous UID and marks the old one as rotated in the wristband ledger — history is preserved, scans after this point resolve to the new band.',
                    )}
                  </div>
                </div>
              ) : null}

              {phase === 'success' && (
                <div className="rounded-lg bg-success/10 border border-success/20 px-4 py-3 text-center space-y-1">
                  <p className="text-sm font-semibold text-success">
                    {t('nfc.sync.successTitle')}
                  </p>
                  {successWristband && (
                    <p className="text-xs text-muted-foreground font-mono">
                      ID: {successWristband.nfcUid}
                    </p>
                  )}
                </div>
              )}

              <div className="flex gap-2 justify-end">
                {phase === 'success' && (
                  <Button onClick={handleClose}>{t('common.close')}</Button>
                )}
                {phase === 'steps' && skippable && (
                  <Button variant="ghost" onClick={() => { abortRef.current.aborted = true; onSkip?.(); }}>
                    <SkipForward className="mr-1.5 h-4 w-4" />
                    {t('common.skip')}
                  </Button>
                )}
                {phase === 'steps' && (
                  <Button variant="outline" onClick={handleClose}>
                    {t('common.cancel')}
                  </Button>
                )}
              </div>
            </>
          )}

          {/* ── Phase: Error ──────────────────────────────────────────────── */}
          {phase === 'error' && (
            <div className="space-y-4">
              <div className="space-y-4">
                {steps.map((step, i) => (
                  <StepRow key={step.id} step={step} index={i} />
                ))}
              </div>

              <div className="rounded-lg bg-destructive/10 border border-destructive/20 px-4 py-3">
                <p className="text-sm text-destructive">{globalError}</p>
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={handleClose}>{t('common.cancel')}</Button>
                <Button onClick={handleRetry}>
                  <RefreshCw className="mr-1.5 h-4 w-4" />
                  {t('common.retry')}
                </Button>
              </div>
            </div>
          )}

        </div>
      </DialogContent>
    </Dialog>
  );
}
